import { fabric } from 'fabric';
import EmbeddedTextBehaviorsV2, {
  type TextProps,
} from './EmbeddedTextBehaviorsV2';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import HighlightBehaviors from './HighlightBehaviors';
import { isTable } from './guards';
import { type IPoint } from 'fabric/fabric-impl';
import { type HasEmbeddedText, type Rect } from '../types';

export const TEXTFIELD_PROPS = [
  'textContent',
  'textAlign',
  'verticalAlign',
  'fontSize',
  'textOpacity',
  'textColor',
];

const TEXTFIELD_SERIALIZED_KEYS = TEXTFIELD_PROPS.filter(
  key => key !== 'textColor'
);

interface TextField
  extends fabric.Object,
    EmbeddedTextBehaviorsV2,
    HasEmbeddedText,
    HighlightBehaviors {
  key: string;
}

@EmbeddedTextBehaviorsV2
@HighlightBehaviors
class TextField extends fabric.Object implements TextField {
  type = 'textField';
  parent: fabric.Object;
  // Avoid infinite loop when updating dimensions of text field
  setFromParent: boolean;
  key: string;

  constructor(
    parent: fabric.Object,
    dimensions: Rect,
    options: {
      [key: string]: unknown;
      textContent?: string;
      textProps: TextProps;
    }
  ) {
    super({});
    this.parent = parent;
    this.textProps = { ...options.textProps };

    Object.assign(this, options);
    super.setOptions(options);
    Object.assign(this, dimensions);

    this.addText(options.textContent ?? '');

    this.parent.on('deselected', () => this.endTextEditing());
    this.parent.on('moving', () => this.endTextEditing());
    this.parent.on('scaling', () => this.endTextEditing());
    this.parent.on('rotating', () => this.endTextEditing());
  }

  containsAbsolutePointer(parent: fabric.Object, pointer: IPoint) {
    const { left, top, width, height } = this;
    const parentLeft = left + parent.left;
    const parentTop = top + parent.top;
    return (
      pointer.x > parentLeft &&
      pointer.y > parentTop &&
      pointer.x < parentLeft + width &&
      pointer.y < parentTop + height
    );
  }

  updateDimensions({
    left,
    top,
    width,
    height,
  }: {
    left: number;
    top: number;
    width: number;
    height: number;
  }) {
    // Update min width and heightwhen parent updates dimensions
    if (this.textProps.wrapText === 'wrapGrow') {
      this.minWidth = width;
      this.minHeight = height;
    }

    this.setFromParent = true;
    this.set('left', left);
    this.set('top', top);
    this.set('width', width);
    this.set('height', height);
    this.setFromParent = false;

    if (
      this.textContent != null &&
      this.textContent !== this.defaultContent()
    ) {
      this.syncTextImage();
    }
  }

  hasEmbeddedText() {
    return true;
  }

  calcTextMargin() {
    return {
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    };
  }

  shouldUpdateParent(keyOrPath: KeyPath, prevValue, currentValue) {
    return (
      keyOrPath === 'height' &&
      prevValue > currentValue &&
      this.textProps.wrapText === 'wrapGrow'
    );
  }

  onTextEditingStart(): void {
    // apply default from parent for props that haven't been set yet
    const parentProps = this.parent.textFieldDefaultProps?.();
    const defaults = {
      ...this.textFieldDefaultProps?.(),
      ...parentProps,
    };

    if (defaults) {
      Object.keys(defaults).forEach(key => {
        if (!this[key]) {
          this.set(key, defaults[key]);
        }
      });
    }
  }

  _set(keyOrPath: KeyPath, value: unknown): fabric.Object {
    const prevValue = value;
    const currentValue = this.height;

    super._set(keyOrPath, value);

    if (
      !this.setFromParent &&
      this.shouldUpdateParent(keyOrPath, prevValue, currentValue)
    ) {
      if (isTable(this.parent)) {
        this.parent.textFieldUpdateDimensions(this, keyOrPath, value);
      }
    }

    if (
      typeof keyOrPath === 'string' &&
      TEXTFIELD_SERIALIZED_KEYS.includes(keyOrPath)
    ) {
      this.parent.set(['textFields', this.key, keyOrPath], value);

      if (keyOrPath === 'textContent') {
        this.setTextContent(value as string);
      }
    }

    return this;
  }

  get selectedTextFields() {
    return [];
  }
}

export default TextField;
